/**
 * AppHost — one persistent, cross-domain App page + a thin IPC client.
 *
 * Every App (the built-in TypeScript Console and workspace Apps alike) runs the
 * standard App-page pipeline: the consumer's `publishShell` returns the iframe
 * `src` on a dedicated, per-app **origin** where the **server** builds the shell
 * document — an `allow-scripts allow-same-origin` frame (cross-domain from this
 * host, so it can't strip its own sandbox). The page-runtime hands the host
 * objects (`playground` + `capabilities` + `host`) to a program page as an
 * `AppContext` passed into its `mount`/tools (a raw-HTML page gets them on
 * `window` instead). The page owns its REPL and asks the parent only to *build*
 * code through a `compile` capability — there is no bespoke `repl_compile`
 * protocol.
 *
 * Lifecycle (driven by {@link AppHostRegistry}): a host is created lazily (on
 * open OR the agent's first tool call), parked off-screen so it survives route
 * changes, and disposed only on an explicit close.
 *
 * The host exposes a generic `post`/`on` bus (over the sandbox host's IPC) plus
 * a `callTool(name, input)` helper that drives the generic agent-tool round-trip
 * (`agent_tool_call` → `agent_tool_result`/`agent_tool_error`). Any App tool the
 * model invokes is dispatched this way; the App's page answers it with the
 * module export named after the tool (the built-in console exports `eval`). An
 * App that exports no function for the called name gets an immediate "no such
 * export" error rather than a hung turn.
 */
import type { CapabilityProviders } from "playground/apps/capabilities";
import type { AppIndexEntry } from "playground/apps/index/app-index";
import {
  appPermissionsPolicy,
  appSandbox,
  STANDARD_PAGE_RUNTIME,
  type DevicePermission,
} from "playground/apps/manifest";
import type { Playground } from "playground/hal/index";
import { createSandboxHost, type SandboxHost } from "playground/sandbox";
import {
  makeAppFsGuard,
  type AppFsScope,
  type FsGuard,
} from "playground/sandbox/app-fs-guard";
import type { ReplicaHost } from "playground/sandbox/replica-host";

import type { ToolCallResult } from "./types";

/**
 * What the host needs to know per App, resolved by the registry. The shell
 * document itself is built **server-side** now (the consumer's `publishShell`
 * returns its URL), so this only carries the parent-side bits: the capability
 * providers to apply on attach, and the host stylesheet to link.
 */
export interface AppLaunchSpec {
  /** Parent-side capability providers the App declared (e.g. `compile`). */
  capabilityProviders?: CapabilityProviders;
  /**
   * Browser **device permissions** (`capabilities.permissions`) the App
   * declared. The host delegates exactly these to the cross-origin App iframe
   * via its `allow` attribute (see {@link AppHost.allow}). App-level, so stable
   * across views; the first launch fixes them.
   */
  permissions?: DevicePermission[];
  /**
   * Extra iframe `sandbox` tokens (`capabilities.sandbox`) the App requested
   * beyond the always-on base floor — e.g. `allow-popups`, `allow-modals`. The
   * host merges them into the frame's `sandbox` attribute (see
   * {@link AppHost.sandbox}). App-level + stable across views; the first launch
   * fixes them.
   */
  sandbox?: string[];
  /** Host stylesheet URL the server should `<link>` into the shell so the page
   *  looks native (`theme:"system"`). Omitted ⇒ no theme. */
  themeUrl?: string;
  /**
   * The module specifier the in-frame runtime imports to render this view — an
   * `@/…` path the page's importmap resolves (e.g. `@/Apps/<name>/tile.js`).
   * Used to **swap** entrypoints in the single iframe via the `render_program`
   * message (no reload). Absent ⇒ this view can't be swapped to in-frame (e.g.
   * an `html` page); the initial view still loads via the shell.
   */
  programSpecifier?: string;
  /**
   * The App's filesystem confinement (SUP-181). Omitted ⇒ rooted at the App's
   * identity path with no data dir and no grants (the default lockdown). The
   * resolver supplies the data dir + the MEMBER-APPROVED grants from the
   * host's grant store — never the manifest's own ask; there is no trusted/
   * workspace mode.
   */
  filesystem?: AppFsScope;
}

export interface CreateAppHostOptions {
  /** Stable id (the App name); also the tool-call id prefix. */
  id: string;
  /** Workspace Playground (file IPC + the host's change feed for `watch`). */
  playground: Playground;
  /**
   * Resolve this App's launch spec for a given **view** (the rendered entrypoint
   * runtime, e.g. `"page"` or `"hearth:pinboard"`); null ⇒ not launchable. Reads
   * the manifest; returns the per-view theme + swap specifier.
   */
  resolveLaunchSpec: (
    id: string,
    view: string,
  ) => Promise<AppLaunchSpec | null> | AppLaunchSpec | null;
  /**
   * Resolve the **app-origin URL** the iframe should load for `appId`'s initial
   * `view`. App pages run on a dedicated, per-app origin (`allow-same-origin`,
   * cross-domain from the host) and the shell is built **server-side** — the
   * consumer (e.g. Hearth's `POST /api/apps/shell`) mints a token and returns the
   * shell URL (`<appOrigin>/api/apps/<owner>/<name>/shell.html?t=…&view=…`).
   * `themeUrl` (from the launch spec) is forwarded so the server can `<link>` the
   * host stylesheet.
   */
  publishShell: (
    appId: string,
    themeUrl: string | undefined,
    view: string,
  ) => Promise<string>;
  /**
   * The rendered entrypoint the iframe loads **first** (e.g. `"page"` or
   * `"hearth:pinboard"`). The host swaps to other views in place via
   * {@link AppHost.setView}. Defaults to the standard `page` runtime.
   */
  initialView?: string;
  /** Total agent tool-call timeout. Defaults to 10 minutes. */
  evalTimeoutMs?: number;
  /**
   * Serves each page's document replica. Without it, a page's text or
   * structured open fails (see `playground/sandbox/replica-host`).
   */
  replica?: ReplicaHost;
}

export interface AppHost {
  readonly id: string;
  /**
   * The app-origin URL the iframe loads (built shell + published to the app
   * origin). Memoized until a non-program view swap rebuilds it. The frame sets
   * it as `src`.
   */
  src(): Promise<string>;
  /**
   * Subscribe to shell-`src` rebuilds. The host rebuilds (and reloads) the shell
   * when swapping to a raw-HTML view, which can't be rendered in-frame; the frame
   * re-reads {@link AppHost.src} and reloads the iframe.
   */
  subscribeSrc(listener: () => void): () => void;
  /**
   * The Permissions-Policy string for the iframe's `allow` attribute, derived
   * from the App's declared `capabilities.permissions` (`""` when none). Valid
   * once the shell has been resolved — i.e. after {@link AppHost.src} settles
   * (both await the same launch spec). App-level + stable, so it never changes
   * across view swaps.
   */
  allow(): string;
  /**
   * The value for the iframe's `sandbox` attribute — the always-on base floor
   * (`allow-scripts allow-same-origin allow-forms`) plus the App's declared
   * `capabilities.sandbox` extras. Like {@link AppHost.allow}, valid once the
   * shell has resolved (after {@link AppHost.src} settles); App-level + stable,
   * so it never changes across view swaps. Never empty — an App always gets at
   * least the floor.
   */
  sandbox(): string;
  isConnected(): boolean;
  subscribeConnection(listener: (connected: boolean) => void): () => void;
  /**
   * Swap which rendered entrypoint the single iframe shows (e.g. `"page"` ⇄
   * `"hearth:pinboard"`) in place — no reload. Posts `render_program` once the
   * page is ready; a no-op if `view` is already shown.
   */
  setView(view: string): void;
  /** Post a custom parent → child message. */
  post(type: string, data?: Record<string, unknown>): void;
  /** Subscribe to a child → parent message type. Survives iframe re-attach. */
  on<M = unknown>(type: string, handler: (msg: M) => void): () => void;
  /**
   * Dispatch one agent tool call into the App's page and await its result. The
   * module named `name` must be exported by `module` (the tool's `entrypoint`
   * specifier) — or, when omitted, by the App's `page` entrypoint (the default
   * tool home); if not, resolves `{ ok: false }` with a "no such export" error.
   */
  callTool(
    name: string,
    input: unknown,
    module?: string,
  ): Promise<ToolCallResult>;
  /** Attach/detach the iframe element (called by the frame component). */
  attachIframe(iframe: HTMLIFrameElement | null): void;
  dispose(): void;
}

const DEFAULT_TOOL_TIMEOUT_MS = 10 * 60_000;

/** Re-mint the memoized shell URL on the next `src()` read once it's this old.
 *  The shell token is long-lived (days) and gates only the shell document —
 *  modules are SW-served, so nothing expires in a *running* app — but a host
 *  parked in a very long-lived tab could otherwise reload its iframe with an
 *  expired URL and 403 with no runtime alive to recover. Render-driven (no
 *  timer): a re-mint only happens when the frame actually re-reads `src`,
 *  and a fresh URL only takes effect on an iframe (re)load that was happening
 *  anyway. Kept well under the consumer's token TTL. */
const SRC_REMINT_AFTER_MS = 24 * 60 * 60 * 1000;

type Pending = {
  /** Always resolved (never rejected) with a structured pass/fail result. */
  resolve: (v: ToolCallResult) => void;
  timer: ReturnType<typeof setTimeout>;
};

export function createAppHost(options: CreateAppHostOptions): AppHost {
  const {
    id,
    playground,
    resolveLaunchSpec,
    publishShell,
    initialView = STANDARD_PAGE_RUNTIME,
    evalTimeoutMs = DEFAULT_TOOL_TIMEOUT_MS,
  } = options;

  // The single iframe loads `initialView` via its server-built shell; `shownView`
  // tracks what the iframe currently renders (it resets to `initialView` on every
  // (re)attach, since a reload re-runs the baked initial program), and
  // `desiredView` is the last view the host asked for. `setView` reconciles them
  // by posting `render_program` once the page is ready.
  let shownView = initialView;
  let desiredView = initialView;
  // Which view the current shell URL bakes — what the iframe renders after a
  // (re)load. Program swaps change `shownView` in-frame without moving this; a
  // raw-HTML target can't swap in-frame, so we rebuild the shell for it and
  // reload, which moves `bakedView` (see `maybeSwap`).
  let bakedView = initialView;

  let host: SandboxHost | null = null;
  let hostUnsubs: Array<() => void> = [];
  // Tool-call ids are prefixed per attach so a pending entry can never collide
  // with one from a previous iframe generation.
  let callIdPrefix = `${id}:0`;
  let attachCount = 0;
  let rpcCounter = 0;

  // App-registered child→parent handlers, re-subscribed to each fresh host so
  // an iframe swap (StrictMode mount/cleanup, route remount) doesn't drop them.
  const handlers = new Map<string, Set<(msg: unknown) => void>>();
  const pending = new Map<string, Pending>();

  const connectionListeners = new Set<(connected: boolean) => void>();
  let connected = false;

  function setConnected(next: boolean) {
    if (connected === next) return;
    connected = next;
    for (const l of connectionListeners) l(next);
  }

  // `connected` is the transport handshake — it fires *before* the page program
  // loads (the runtime imports it after the handshake). `page_ready` fires once
  // the program's top-level has run, so its tool exports (e.g. the console's
  // `eval`) are bound. `callTool` gates on this, not the handshake, or the
  // `agent_tool_call` message races ahead of the export and is dropped — leaving
  // the call to hang until it times out.
  const readyListeners = new Set<() => void>();
  let pageReady = false;

  function setPageReady() {
    if (pageReady) return;
    pageReady = true;
    for (const l of readyListeners) l();
    // The initial program is live (so is the runtime's `render_program` handler);
    // reconcile to the desired view if a swap was requested before ready, or a
    // reconnect reset us to the baked initial view.
    void maybeSwap();
  }

  // Build the page shell + publish it to the app origin once, lazily. The shell
  // HTML is the same document we used to inline as a `srcDoc`; now it's served
  // from a dedicated per-app origin (so the iframe can be `allow-same-origin`
  // cross-domain) and the host loads that URL as `src`. `capabilityProviders`
  // come from the resolved spec and are applied on attach.
  let providers: CapabilityProviders | undefined;
  // The iframe's `allow` attribute (Permissions Policy), derived from the App's
  // `capabilities.permissions`. Resolved as a side effect of `buildShell`, so
  // it's set by the time `src()` settles; the frame reads it to delegate exactly
  // the declared device permissions to the cross-origin App page. App-level, so
  // a view swap rebuilds the same value.
  let permissionsPolicy = "";
  let fsGuard: FsGuard | undefined;
  // The iframe's `sandbox` attribute — the base floor plus the App's declared
  // `capabilities.sandbox` extras. Resolved alongside `permissionsPolicy` in
  // `buildShell`; App-level, so a view swap recomputes the same value. Seeded
  // with the floor so it's a valid attribute even before the shell resolves.
  let sandboxAttr = appSandbox(undefined);
  let srcPromise: Promise<string> | null = null;
  const srcListeners = new Set<() => void>();

  // The module that exports the App's agent tools. Tools are app-level, so they
  // live on a stable entrypoint (the standard page), NOT whichever view the
  // single iframe renders (e.g. a `hearth:pinboard` tile that exports no tools).
  // `undefined` if the App has no page entrypoint; the runtime then falls back
  // to the rendered view's exports.
  let toolModule: string | undefined;

  // The app-index entry the current shell was built for, whether that build
  // launched or failed. Everything a launch fixes (capability providers, CSP,
  // tool module) derives from it, so a changed entry (a re-pointed built-in
  // mount, an edited or repaired manifest) relaunches the App.
  let launchedFrom: { entry: AppIndexEntry | undefined } | null = null;
  const unsubscribeIndex = playground.apps.subscribe(() => {
    const entry = playground.apps.get(id);
    if (!launchedFrom || entry === launchedFrom.entry) return;
    launchedFrom = { entry };
    reloadShell(bakedView);
  });
  // Only the newest build may set what a launch fixes; an older one defers to it.
  let builds = 0;

  // Build the server shell for `view` from the App's manifest; hand it the theme
  // link + the view and get back the app-origin URL the iframe loads.
  function buildShell(view: string): Promise<string> {
    const build = ++builds;
    return (async () => {
      // Stamp the entry BEFORE resolving: the resolver awaits (consent), and a
      // re-point landing meanwhile must relaunch, not pass as what was built.
      await playground.apps.ready();
      launchedFrom = { entry: playground.apps.get(id) };
      // One resolution for the shell AND the tool module, so a page never runs
      // tool code from a newer manifest than its capabilities came from.
      const [spec, pageSpec] = await Promise.all([
        Promise.resolve().then(() => resolveLaunchSpec(id, view)),
        Promise.resolve()
          .then(() => resolveLaunchSpec(id, STANDARD_PAGE_RUNTIME))
          .catch(() => null),
      ]);
      if (build !== builds) return src();
      if (!spec) throw new Error(`App "${id}" is not launchable`);
      toolModule = pageSpec?.programSpecifier;
      providers = spec.capabilityProviders;
      permissionsPolicy = appPermissionsPolicy(spec.permissions);
      // Confine the App's FS to its scope — install dir + data dir + the
      // member-approved grants the resolver supplied. Every App is confined;
      // default when unspecified: rooted at the App's identity path (SUP-181).
      fsGuard = makeAppFsGuard(spec.filesystem ?? { root: id });
      sandboxAttr = appSandbox(spec.sandbox);
      return publishShell(id, spec.themeUrl, view);
    })();
  }

  let srcMintedAt = 0;

  function src(): Promise<string> {
    if (!srcPromise || Date.now() - srcMintedAt >= SRC_REMINT_AFTER_MS) {
      srcMintedAt = Date.now();
      srcPromise = buildShell(bakedView);
    }
    return srcPromise;
  }

  // Rebuild the shell for `view` and notify the frame to reload the iframe with
  // the new `src`. Used for raw-HTML view targets, which own their whole document
  // (baked into the shell server-side) and so can't be swapped in-frame, and to
  // relaunch an App whose index entry changed.
  function reloadShell(view: string): void {
    bakedView = view;
    srcMintedAt = Date.now();
    srcPromise = buildShell(view);
    for (const l of srcListeners) l();
  }

  function subscribeSrc(listener: () => void): () => void {
    srcListeners.add(listener);
    return () => srcListeners.delete(listener);
  }

  function subscribeHostHandler(type: string): () => void {
    // One host subscription per type; it fans out to every app handler.
    return host!.on<unknown>(type, (msg) => {
      const set = handlers.get(type);
      if (!set) return;
      for (const cb of set) cb(msg);
    });
  }

  function attachIframe(iframe: HTMLIFrameElement | null) {
    if (host) {
      for (const u of hostUnsubs) u();
      hostUnsubs = [];
      host.dispose();
      host = null;
    }
    setConnected(false);
    // A fresh iframe means a fresh program load (or, on re-attach to an
    // already-loaded page, the runtime re-announces `page_ready` on its next
    // handshake — see page-runtime). Either way, readiness is re-established
    // below, so clear the latch. A (re)load re-runs whatever view the current
    // shell bakes, so the iframe is showing `bakedView` again until
    // `setPageReady` reconciles.
    pageReady = false;
    shownView = bakedView;
    if (!iframe) return;

    attachCount += 1;
    callIdPrefix = `${id}:${attachCount}`;
    host = createSandboxHost({
      iframe,
      playground,
      fsGuard,
      capabilityProviders: providers,
      replica: options.replica,
    });
    hostUnsubs.push(host.onConnectionChange(setConnected));
    hostUnsubs.push(host.on("page_ready", setPageReady));
    // Re-subscribe every app handler + the eval result/error handlers.
    for (const type of handlers.keys())
      hostUnsubs.push(subscribeHostHandler(type));
    hostUnsubs.push(
      host.on<{ id: string; output: unknown }>("agent_tool_result", (m) => {
        const p = pending.get(m.id);
        if (!p) return;
        clearTimeout(p.timer);
        pending.delete(m.id);
        p.resolve({ ok: true, output: m.output });
      }),
    );
    hostUnsubs.push(
      host.on<{ id: string; error: string }>("agent_tool_error", (m) => {
        const p = pending.get(m.id);
        if (!p) return;
        clearTimeout(p.timer);
        pending.delete(m.id);
        p.resolve({ ok: false, error: m.error });
      }),
    );
  }

  function whenPageReady(): Promise<void> {
    if (pageReady) return Promise.resolve();
    return new Promise<void>((resolve) => {
      const listener = () => {
        readyListeners.delete(listener);
        resolve();
      };
      readyListeners.add(listener);
    });
  }

  function post(type: string, data: Record<string, unknown> = {}) {
    host?.post({ kind: "parent", type, ...data });
  }

  function on<M = unknown>(
    type: string,
    handler: (msg: M) => void,
  ): () => void {
    let set = handlers.get(type);
    if (!set) {
      set = new Set();
      handlers.set(type, set);
      // Subscribe lazily on the live host (if any) for this new type.
      if (host) hostUnsubs.push(subscribeHostHandler(type));
    }
    set.add(handler as (msg: unknown) => void);
    return () => set!.delete(handler as (msg: unknown) => void);
  }

  // Reconcile the iframe to `desiredView` by swapping the entrypoint in place
  // (no reload). Gated on page-ready so the runtime's `render_program` handler +
  // the initial program are live. `swapping` serializes overlapping requests.
  let swapping = false;
  async function maybeSwap(): Promise<void> {
    if (!pageReady || swapping) return;
    const want = desiredView;
    if (want === shownView) return;
    swapping = true;
    try {
      const spec = await resolveLaunchSpec(id, want);
      if (desiredView !== want) return; // superseded while resolving
      if (!spec) return; // not launchable for this view — leave the frame as-is
      if (spec.programSpecifier) {
        // Program view: swap the entrypoint in place — no reload.
        post("render_program", { programUrl: spec.programSpecifier });
      } else {
        // Raw-HTML view: it owns its whole document and is baked into the shell
        // server-side, so there's no program to `render_program`. Rebuild the
        // shell for `want` and reload the iframe to show it.
        reloadShell(want);
      }
      shownView = want;
    } finally {
      swapping = false;
    }
    if (desiredView !== shownView) void maybeSwap(); // a change landed mid-swap
  }

  function setView(view: string): void {
    desiredView = view;
    void maybeSwap();
  }

  // Dispatch any agent tool call into the page. The transport (browser iframe)
  // is one detail of *how* a page tool runs; the contract is just
  // `(name, input) → ToolCallResult`, so a worker-backed variant can slot in
  // behind this same call without touching callers.
  function callTool(
    name: string,
    input: unknown,
    module?: string,
  ): Promise<ToolCallResult> {
    const callId = `${callIdPrefix}:rpc:${rpcCounter++}`;
    return new Promise<ToolCallResult>((resolve) => {
      const timer = setTimeout(() => {
        pending.delete(callId);
        resolve({
          ok: false,
          error: `tool "${name}" timed out after ${evalTimeoutMs}ms`,
        });
      }, evalTimeoutMs);
      pending.set(callId, { resolve, timer });
      // Ensure the page is built + published + its program has loaded (so its
      // tool exports exist) before posting — not just handshaken. `src()` is the
      // instantiate step (build + publish the App's shell); if it rejects the App
      // can't load at all — not launchable, an unreadable manifest, a shell-server
      // error — and `page_ready` will never fire for an iframe that never loaded.
      // Await it alongside readiness so that failure fails the tool call fast (via
      // the rejection branch below) instead of hanging until the eval timeout.
      // An explicit `module` (the tool's `entrypoint`) wins; otherwise fall back
      // to the App's page entrypoint (the default tool home).
      void Promise.all([src(), whenPageReady()]).then(
        () => {
          if (!pending.has(callId)) return; // settled by dispose meanwhile
          // `module` lets the runtime find the tool on its own entrypoint even
          // when the iframe is currently rendering a different view.
          post("agent_tool_call", {
            id: callId,
            name,
            input,
            module: module ?? toolModule,
          });
        },
        (err: unknown) => {
          if (!pending.has(callId)) return;
          clearTimeout(timer);
          pending.delete(callId);
          resolve({
            ok: false,
            error: err instanceof Error ? err.message : String(err),
          });
        },
      );
    });
  }

  function dispose() {
    unsubscribeIndex();
    for (const p of pending.values()) {
      clearTimeout(p.timer);
      p.resolve({ ok: false, error: "app host disposed" });
    }
    pending.clear();
    for (const u of hostUnsubs) u();
    hostUnsubs = [];
    host?.dispose();
    host = null;
    setConnected(false);
    connectionListeners.clear();
    pageReady = false;
    readyListeners.clear();
    handlers.clear();
    srcListeners.clear();
  }

  return {
    id,
    src,
    subscribeSrc,
    allow: () => permissionsPolicy,
    sandbox: () => sandboxAttr,
    isConnected: () => connected,
    subscribeConnection(listener) {
      connectionListeners.add(listener);
      listener(connected);
      return () => connectionListeners.delete(listener);
    },
    setView,
    post,
    on,
    callTool,
    attachIframe,
    dispose,
  };
}

export type { ToolCallResult } from "./types";
